export {};
// Bosco & Co storefront behaviour. Progressive enhancement over server-rendered
// HTML: no framework, event delegation, state in localStorage.

type Pet = 'dog' | 'cat' | '';
interface CartItem { v: string; q: number; note?: string; pid?: string }
interface CartState { items: CartItem[]; shipping: 'standard' | 'express' | 'pickup'; code: string; pickup: { name: string; phone: string }; email: string; marketing: boolean }
interface QuoteLine { v: string; productId: string; slug: string; name: string; variantName: string; image: string | null; qty: number; maxQty: number | null; unitCents: number; lineCents: number; discountCents: number; note?: string }
interface Quote { lines: QuoteLine[]; problems: { v: string; message: string }[]; subtotalCents: number; discountCents: number; itemsCents: number; shippingCents: number; totalCents: number; freeShippingRemainingCents: number; shipping: CartState['shipping']; code: { code: string; label: string; applied: boolean } | null }
interface ApiVariant { id: string; name: string; values: string[]; price: number; sale: number | null; stock: number | null; available: boolean; image?: string | null }
interface Palette { ink: string; bg: string; deep: string }
interface ApiProduct { id: string; slug: string; name: string; group: string; inStock: boolean; pending?: boolean; priceMin: number; priceMax: number; saleMin: number | null; discountPercent: number | null; totalStock: number | null; personalised: boolean; image: string | null; images: string[]; options: { name: string; values: string[] }[]; variants: ApiVariant[]; cutout?: { url: string; w: number; h: number } | null; palette?: Palette | null }
interface SetPieceData { id: string; name: string; price: number; sizeIndex: number; options: { name: string; values: string[] }[]; variants: { id: string; values: string[]; price: number; sale: number | null; stock: number | null; available: boolean }[] }

const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector<T>(sel as string) as T | null;
const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document) => [...root.querySelectorAll<T>(sel as string)] as T[];
const money = (c: number) => (c % 100 === 0 ? `$${c / 100}` : `$${(c / 100).toFixed(2)}`);
const esc = (s: string) => s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!);
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

const siteCfg = (() => {
  try {
    return JSON.parse($('#site-config')?.textContent || '{}') as { free: number; standard: number; express: number; pickup: boolean; afterpay: boolean; v: string; ga?: { id?: unknown; host?: unknown } };
  } catch {
    return { free: 10000, standard: 850, express: 0, pickup: true, afterpay: true, v: '' };
  }
})();

/* ---------------- analytics ---------------- */

// Google Analytics 4 without the inline snippet (the CSP allows no inline script): the standard
// dataLayer queue lives here and gtag.js is fetched once the page is idle. A product page that was
// prerendered on hover loads it only when the shopper actually opens the page.
interface GaItem { item_id?: string; item_name: string; item_variant?: string; item_category?: string; price: number; quantity: number }
const gaId = typeof siteCfg.ga?.id === 'string' && /^G-[A-Z0-9]{4,20}$/.test(siteCfg.ga.id) && siteCfg.ga.host === location.host ? siteCfg.ga.id : null;
const dataLayer: unknown[] = gaId ? ((window as unknown as { dataLayer?: unknown[] }).dataLayer ??= []) : [];
let gaReady = false;
// gtag.js reads each queued entry as an `arguments` object, not an array.
function gtag(..._args: unknown[]) {
  if (gaId) dataLayer.push(arguments);
}
// Only these query parameters reach Google: search, product options, collection filters and
// campaign tags. Everything else (order references, sign-in links) is dropped.
const GA_PARAMS = /^(q|variant|sort|size|under|sale|page|utm_[a-z]+|gclid|gbraid|wbraid|srsltid|fbclid|ttclid)$/;
function gaUrl(href: string): string {
  try {
    const u = new URL(href);
    if (u.origin !== location.origin) return href;
    for (const k of [...u.searchParams.keys()]) if (!GA_PARAMS.test(k)) u.searchParams.delete(k);
    u.hash = '';
    return u.toString();
  } catch {
    return '';
  }
}
if (gaId) {
  gtag('js', new Date());
  gtag('config', gaId, { page_location: gaUrl(location.href), ...(document.referrer ? { page_referrer: gaUrl(document.referrer) } : {}) });
  const load = () => {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    s.onload = () => (gaReady = true);
    document.head.append(s);
  };
  const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(load, { timeout: 4000 }) : setTimeout(load, 1500));
  const afterLoad = () => (document.readyState === 'complete' ? idle() : addEventListener('load', idle, { once: true }));
  if ((document as Document & { prerendering?: boolean }).prerendering) document.addEventListener('prerenderingchange', afterLoad, { once: true });
  else afterLoad();
}

const dollars = (cents: number) => Math.round(cents) / 100;
/** One GA4 item: `item_id` is the Square variation id, the same id the Google product feed uses. */
function gaItem(p: { name: string; group?: string }, v: { id: string; name?: string; values?: string[]; price: number; sale: number | null }, quantity: number): GaItem {
  const variant = v.name || v.values?.join(' / ');
  return { item_id: v.id, item_name: p.name, ...(variant ? { item_variant: variant } : {}), ...(p.group ? { item_category: p.group } : {}), price: dollars(v.sale ?? v.price), quantity };
}
/** A GA4 ecommerce event in AUD; `value` defaults to the items' total. */
function track(event: string, items: GaItem[], extra: Record<string, unknown> = {}) {
  if (!gaId || !items.length) return;
  const value = dollars(items.reduce((n, i) => n + Math.round(i.price * 100) * i.quantity, 0));
  gtag('event', event, { currency: 'AUD', value, items, ...extra });
}
/** Track, then leave the page once the hit is sent (at most ~1s): used just before handing over to Square. */
function trackThenGo(event: string, items: GaItem[], extra: Record<string, unknown>, url: string) {
  if (!gaReady || !items.length) {
    location.href = url;
    return;
  }
  let gone = false;
  const go = () => {
    if (gone) return;
    gone = true;
    location.href = url;
  };
  track(event, items, { ...extra, event_callback: go, event_timeout: 1000 });
  setTimeout(go, 1200);
}

/* ---------------- storage ---------------- */

const LS = {
  get<T>(k: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(k);
      return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  },
  set(k: string, v: unknown) {
    try {
      localStorage.setItem(k, JSON.stringify(v));
    } catch {
      /* private mode / full storage: the page still works for this visit */
    }
  },
};

const CART_KEY = 'bosco.cart.v1';
const emptyCart = (): CartState => ({ items: [], shipping: 'standard', code: '', pickup: { name: '', phone: '' }, email: '', marketing: false });
let cart: CartState = { ...emptyCart(), ...LS.get<Partial<CartState>>(CART_KEY, {}) };
let favs: string[] = LS.get<string[]>('bosco.fav.v1', []);
let recent: string[] = LS.get<string[]>('bosco.recent.v1', []);
// Everything read back from storage is validated: it can be stale or edited.
const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
let profile: { pet: Pet; size: string } = (() => {
  const p = LS.get<{ pet?: unknown; size?: unknown }>('bosco.pet.v1', {});
  return { pet: p.pet === 'dog' || p.pet === 'cat' ? p.pet : '', size: SIZES.includes(String(p.size)) ? String(p.size) : '' };
})();
cart.items = (Array.isArray(cart.items) ? cart.items : [])
  .filter((i) => i && typeof i.v === 'string' && /^[A-Za-z0-9_#-]{1,64}$/.test(i.v) && Number.isInteger(i.q) && i.q > 0)
  .map((i) => ({ v: i.v, q: Math.min(50, i.q), ...(typeof i.pid === 'string' ? { pid: i.pid } : {}), ...(typeof i.note === 'string' ? { note: i.note.slice(0, 160) } : {}) }));
if (!['standard', 'express', 'pickup'].includes(cart.shipping)) cart.shipping = 'standard';
cart.email = typeof cart.email === 'string' ? cart.email.slice(0, 254) : '';
cart.marketing = cart.marketing === true; // consent is never assumed
favs = favs.filter((x) => typeof x === 'string').slice(0, 100);
recent = recent.filter((x) => typeof x === 'string').slice(0, 12);

function saveCart() {
  LS.set(CART_KEY, cart);
  renderCounts();
}

addEventListener('storage', (e) => {
  if (e.key === CART_KEY) {
    cart = { ...emptyCart(), ...LS.get<Partial<CartState>>(CART_KEY, {}) };
    renderCounts();
    refreshCart();
  }
  if (e.key === 'bosco.fav.v1') {
    favs = LS.get<string[]>('bosco.fav.v1', []);
    renderFavs();
  }
});

/* ---------------- dialogs & toast ---------------- */

let lastFocus: HTMLElement | null = null;
function openDialog(id: string) {
  const d = document.getElementById(id) as HTMLDialogElement | null;
  if (!d || d.open) return;
  // Content kept out of the page until its dialog first opens: the harness fit video's poster is 90 KB, and most shoppers never open the size guide.
  d.querySelectorAll<HTMLTemplateElement>('template[data-on-open]').forEach((t) => t.replaceWith(t.content.cloneNode(true)));
  lastFocus = document.activeElement as HTMLElement;
  d.showModal();
  document.documentElement.style.overflow = 'hidden';
  d.dispatchEvent(new CustomEvent('bosco:open'));
}
function closeDialog(d: HTMLDialogElement) {
  d.close();
}
document.addEventListener('close', (e) => {
  if (!(e.target instanceof HTMLDialogElement)) return;
  // However it closed (button, Esc, backdrop), a video in it stops: the harness fit guide in the size guide.
  e.target.querySelectorAll('video').forEach((v) => v.pause());
  if (!$$<HTMLDialogElement>('dialog').some((x) => x.open)) document.documentElement.style.overflow = '';
  lastFocus?.focus?.();
}, true);
document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  const opener = t.closest<HTMLElement>('[data-open]');
  if (opener) {
    e.preventDefault();
    openDialog(opener.dataset.open!);
    return;
  }
  const closer = t.closest<HTMLElement>('[data-close]');
  if (closer) {
    const d = closer.closest('dialog');
    if (d) closeDialog(d);
    return;
  }
  // Click on the backdrop (the dialog element itself) closes it.
  if (t instanceof HTMLDialogElement && t.open) {
    const r = t.getBoundingClientRect();
    const ev = e as MouseEvent;
    if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) closeDialog(t);
  }
});

let toastTimer = 0;
function toast(html: string) {
  const el = $('[data-toast]');
  if (!el) return;
  el.innerHTML = html;
  el.classList.add('is-shown');
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => el.classList.remove('is-shown'), 3200);
}

/* ---------------- images ---------------- */

let imageResizing = false;
function img(url: string | null, w: number): string {
  if (!url) return '';
  if (imageResizing && url.startsWith('https://')) return `/cdn-cgi/image/width=${w},quality=78,format=auto,fit=scale-down/${url}`;
  if (url.includes('cdn.shopify.com')) return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
  return url;
}
const BOSCO_SVG = (() => document.querySelector('.walk__dog, .bosco, .ph')?.outerHTML ?? '')();

/* ---------------- counts & favourites ---------------- */

function renderCounts() {
  const n = cart.items.reduce((s, i) => s + i.q, 0);
  for (const el of $$('[data-cart-count]')) {
    el.textContent = String(n);
    el.dataset.n = String(n);
  }
  for (const el of $$('[data-fav-count]')) {
    el.textContent = String(favs.length);
    el.dataset.n = String(favs.length);
  }
}
function bump(sel: string) {
  for (const el of $$(sel)) {
    el.classList.remove('is-bumped');
    void el.offsetWidth;
    el.classList.add('is-bumped');
  }
}
function renderFavs() {
  for (const b of $$<HTMLButtonElement>('[data-fav]')) {
    const on = favs.includes(b.dataset.fav!);
    b.setAttribute('aria-pressed', String(on));
    const label = $('[data-fav-label]', b);
    if (label) label.textContent = on ? 'Saved' : 'Save';
  }
  renderCounts();
}
document.addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-fav]');
  if (!b) return;
  e.preventDefault();
  const id = b.dataset.fav!;
  favs = favs.includes(id) ? favs.filter((x) => x !== id) : [id, ...favs].slice(0, 100);
  LS.set('bosco.fav.v1', favs);
  renderFavs();
  syncAccount();
  if (favs.includes(id)) {
    bump('[data-fav-count]');
    toast('Saved to <a href="/favourites">your favourites</a>');
  }
});

/* ---------------- product data (quick add, favourites, recent) ---------------- */

const productCache = new Map<string, ApiProduct>();
async function fetchProducts(ids: string[]): Promise<ApiProduct[]> {
  const need = ids.filter((id) => !productCache.has(id));
  if (need.length) {
    const res = await fetch(`/api/products?ids=${need.map(encodeURIComponent).join(',')}`);
    if (res.ok) {
      const data = (await res.json()) as { products: ApiProduct[]; imageResizing: boolean };
      imageResizing = data.imageResizing;
      for (const p of data.products) productCache.set(p.id, p);
    }
  }
  return ids.map((id) => productCache.get(id)).filter((p): p is ApiProduct => !!p);
}

// Local cutouts have 300 and 600px siblings (tools/cutout-variants.mjs); tiles and thumbnails take the 300.
const cutoutAt = (url: string, w: number) => (url.includes('/_cutouts/') ? url.replace(/\.webp$/, w <= 300 ? '-300.webp' : '-600.webp') : url);
const cutout600 = (url: string) => cutoutAt(url, 600);
const splitName = (name: string): [string, string | null] => {
  const m = /^(.*?)\s[–—-]\s(.+)$/.exec(name);
  return m ? [m[1], m[2]] : [name, null];
};

function cardHtml(p: ApiProduct): string {
  const price = p.saleMin !== null
    ? `<span class="price"><s>${money(p.priceMin)}</s> <strong class="now">${money(p.saleMin)}</strong></span>`
    : `<span class="price">${p.priceMin !== p.priceMax ? '<span class="from">from</span>' : ''}${money(p.priceMin)}</span>`;
  const low = p.inStock && p.totalStock !== null && p.totalStock <= 3;
  const pal = p.palette ?? { ink: '#fc5e13', bg: '#ffe8d6', deep: '#01154d' };
  const [base, print] = splitName(p.name);
  const media = p.cutout
    ? `<img class="pcard__cutout" src="${esc(cutout600(p.cutout.url))}" alt="" width="${p.cutout.w}" height="${p.cutout.h}" loading="lazy" decoding="async" data-fly>`
    : p.image ? `<img class="pcard__photo" src="${esc(img(p.image, 480))}" alt="" width="480" height="480" loading="lazy" decoding="async" data-fly>` : '';
  return `<article class="pcard${p.inStock ? '' : ' pcard--out'}${p.cutout ? ' pcard--cut' : ''}" style="--bg:${esc(pal.bg)};--ink:${esc(pal.ink)};--deep:${esc(pal.deep)}" data-card data-id="${esc(p.id)}"><div class="pcard__stage" data-tilt>
    <a class="pcard__media" href="/products/${esc(p.slug)}" tabindex="-1" aria-hidden="true">${media}<span class="pcard__sheen" aria-hidden="true"></span></a>
    <div class="card__badges">${p.discountPercent ? `<span class="badge badge--sale">${p.discountPercent}% off</span>` : ''}${low ? `<span class="badge badge--low">Only ${p.totalStock} left</span>` : ''}${p.inStock ? '' : `<span class="badge badge--out">${p.pending ? 'Back soon' : 'Sold out'}</span>`}</div>
    <button class="card__fav" type="button" data-fav="${esc(p.id)}" aria-pressed="${favs.includes(p.id)}" aria-label="Save ${esc(p.name)} to favourites"><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 20s-7.5-4.6-9.2-9.3C1.7 7.5 3.8 4.5 7 4.5c2 0 3.4 1.1 5 3 1.6-1.9 3-3 5-3 3.2 0 5.3 3 4.2 6.2C19.5 15.4 12 20 12 20z"/></svg></button>
    ${p.inStock ? `<button class="card__add" type="button" data-quick-add="${esc(p.id)}" aria-label="Quick add ${esc(p.name)}"><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 5v14"/><path d="M5 12h14"/></svg></button>` : ''}</div>
    <div class="pcard__body"><h3 class="card__title"><a href="/products/${esc(p.slug)}">${esc(base)}${print ? `<span class="card__print">${esc(print)}</span>` : ''}</a></h3><div class="card__meta">${price}</div></div>
  </article>`;
}

/* ---------------- variant selection (shared by product page + quick add) ---------------- */

type Sellable = { values: string[]; available: boolean };
function variantFor<V extends Sellable>(p: { options: { name: string }[]; variants: V[] }, chosen: (string | null)[]): V | null {
  if (!p.options.length) return p.variants[0] ?? null;
  if (chosen.some((c) => c === null)) return null;
  return p.variants.find((v) => v.values.every((val, i) => val === chosen[i])) ?? null;
}
function valueAvailable(p: { options: { name: string }[]; variants: Sellable[] }, chosen: (string | null)[], optIndex: number, value: string): boolean {
  return p.variants.some((v) => v.available && v.values[optIndex] === value && v.values.every((val, i) => i === optIndex || chosen[i] === null || chosen[i] === val));
}

/* ---------------- the fetch: product arcs into the bag, Bosco chases ---------------- */

const PAW_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><ellipse cx="12" cy="16.5" rx="5" ry="4"/><circle cx="5.5" cy="10.5" r="2.3"/><circle cx="18.5" cy="10.5" r="2.3"/><circle cx="8.8" cy="6" r="2.3"/><circle cx="15.2" cy="6" r="2.3"/></svg>';

function pawBurst(layer: HTMLElement, cx: number, cy: number) {
  for (let i = 0; i < 6; i++) {
    const p = document.createElement('span');
    p.className = 'paw';
    p.innerHTML = PAW_SVG;
    layer.append(p);
    const ang = (Math.PI * 2 * i) / 6 + Math.random() * 0.7;
    const d = 26 + Math.random() * 24;
    p.animate(
      [
        { transform: `translate(${cx - 8}px, ${cy - 8}px) scale(0.4) rotate(${ang}rad)`, opacity: 1 },
        { transform: `translate(${cx - 8 + Math.cos(ang) * d}px, ${cy - 8 + Math.sin(ang) * d}px) scale(1) rotate(${ang}rad)`, opacity: 0 },
      ],
      { duration: 560, delay: i * 20, easing: 'cubic-bezier(.2,.8,.3,1)', fill: 'forwards' },
    ).finished.then(() => p.remove(), () => p.remove());
  }
}

/** Flies a copy of the product image into the bag icon. Resolves when it lands. */
function fetchToBag(src: Element | null): Promise<void> {
  const layer = $('[data-fetch-layer]');
  const bag = $('[data-bag]');
  if (!layer || !bag || !(src instanceof HTMLImageElement) || reduceMotion || !('animate' in layer)) return Promise.resolve();
  const from = src.getBoundingClientRect();
  const to = bag.getBoundingClientRect();
  if (!from.width || !to.width || from.bottom < 0 || from.top > innerHeight) return Promise.resolve();
  const ghost = document.createElement('img');
  ghost.className = 'fetch-ghost';
  ghost.src = src.currentSrc || src.src;
  ghost.alt = '';
  ghost.style.width = `${from.width}px`;
  ghost.style.height = `${from.height}px`;
  layer.append(ghost);
  const cx = to.left + to.width / 2, cy = to.top + to.height / 2;
  const endX = cx - from.width / 2, endY = cy - from.height / 2;
  const midX = (from.left + endX) / 2, midY = Math.min(from.top, endY) - Math.max(70, Math.abs(from.top - endY) * 0.2);
  const dur = 720;
  const flight = ghost.animate(
    [
      { transform: `translate(${from.left}px, ${from.top}px) scale(1) rotate(0deg)`, opacity: 1 },
      { transform: `translate(${midX}px, ${midY}px) scale(0.55) rotate(-8deg)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${endX}px, ${endY}px) scale(0.06) rotate(8deg)`, opacity: 0.4 },
    ],
    { duration: dur, easing: 'cubic-bezier(.3,.7,.3,1)', fill: 'forwards' },
  );
  // Bosco runs after it, a beat behind.
  const dog = document.createElement('span');
  dog.className = 'fetch-dog';
  dog.innerHTML = BOSCO_SVG;
  layer.append(dog);
  const dw = 46, dh = 33;
  const sx = from.left + from.width / 2 - dw / 2, sy = from.top + from.height - dh;
  const ex = cx - dw / 2, ey = cy - dh / 2;
  const face = ex < sx ? ' scaleX(-1)' : '';
  dog.animate(
    [
      { transform: `translate(${sx}px, ${sy}px)${face}`, opacity: 0 },
      { transform: `translate(${sx}px, ${sy}px)${face}`, opacity: 1, offset: 0.12 },
      { transform: `translate(${(sx + ex) / 2}px, ${Math.min(sy, ey) - 30}px)${face} scale(0.9)`, opacity: 1, offset: 0.6 },
      { transform: `translate(${ex}px, ${ey}px)${face} scale(0.35)`, opacity: 0 },
    ],
    { duration: dur + 140, delay: 110, easing: 'cubic-bezier(.4,.5,.3,1)', fill: 'forwards' },
  ).finished.then(() => dog.remove(), () => dog.remove());
  return flight.finished.then(
    () => {
      ghost.remove();
      pawBurst(layer, cx, cy);
      bag.classList.remove('is-caught');
      void bag.offsetWidth;
      bag.classList.add('is-caught');
    },
    () => ghost.remove(),
  );
}

/* ---------------- add to cart ---------------- */

function putInCart(v: string, q: number, pid: string, note?: string) {
  const existing = cart.items.find((i) => i.v === v && (i.note ?? '') === (note ?? ''));
  if (existing) existing.q = Math.min(50, existing.q + q);
  else cart.items.push({ v, q, pid, ...(note ? { note } : {}) });
}

function addToCart(v: string, q: number, pid: string, note?: string, from?: Element | null) {
  putInCart(v, q, pid, note);
  saveCart();
  refreshCart(true);
  fetchToBag(from ?? null).then(() => {
    bump('[data-cart-count]');
    if (!$('[data-cart-page]')) openDialog('cart');
  });
}

/** The image to fly from, for a button inside a card, lookbook item or the product gallery. */
const flySource = (el: Element): Element | null =>
  el.closest('[data-card], .look, .set__item, .bpiece')?.querySelector('[data-fly], img') ?? $('[data-gal-main] [data-fly]') ?? null;

/* ---------------- quick add ---------------- */

document.addEventListener('click', async (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-quick-add]');
  if (!b) return;
  e.preventDefault();
  const source = flySource(b);
  const [p] = await fetchProducts([b.dataset.quickAdd!]);
  if (!p) return;
  const buyable = p.variants.filter((v) => v.available);
  if (buyable.length === 1 && !p.personalised) {
    addToCart(buyable[0].id, 1, p.id, undefined, source);
    track('add_to_cart', [gaItem(p, buyable[0], 1)]);
    return;
  }
  if (p.personalised) {
    location.href = `/products/${p.slug}`;
    return;
  }
  const body = $('[data-quick-body]');
  if (!body) return;
  const chosen: (string | null)[] = p.options.map((o) => (o.values.length === 1 ? o.values[0] : null));
  const render = () => {
    const v = variantFor(p, chosen);
    body.innerHTML = `<div class="stack" style="--stack:16px">
      <div class="quick__hero">${tileHtml(p, p.image, 84)}
      <div><strong>${esc(p.name)}</strong><div class="price">${v ? (v.sale !== null ? `<s>${money(v.price)}</s> <strong class="now">${money(v.sale)}</strong>` : money(v.price)) : p.saleMin !== null ? `<s>${money(p.priceMin)}</s> <strong class="now">from ${money(p.saleMin)}</strong>` : `${p.priceMin !== p.priceMax ? 'from ' : ''}${money(p.priceMin)}`}</div></div></div>
      ${p.options.map((o, i) => `<fieldset class="opt"><legend>${esc(o.name)}${chosen[i] ? `: <span class="val">${esc(chosen[i]!)}</span>` : ''}</legend><div class="chips">${o.values.map((val) => {
        const ok = p.pending || valueAvailable(p, chosen, i, val);
        return `<button type="button" class="chip" data-q-opt="${i}" data-q-val="${esc(val)}" aria-pressed="${chosen[i] === val}" ${ok ? '' : 'data-unavailable="true"'}>${esc(val)}</button>`;
      }).join('')}</div></fieldset>`).join('')}
      <button class="btn btn--block" type="button" data-q-add ${v && v.available ? '' : 'disabled'}>${p.pending ? 'Back online soon' : v ? (v.available ? 'Add to cart' : 'Sold out') : `Choose a ${esc(p.options[chosen.findIndex((x) => x === null)]?.name.toLowerCase() ?? 'option')}`}</button>
      <a href="/products/${esc(p.slug)}" class="small">See full details</a></div>`;
  };
  body.onclick = (ev) => {
    const t = ev.target as HTMLElement;
    const opt = t.closest<HTMLElement>('[data-q-opt]');
    if (opt) {
      const i = Number(opt.dataset.qOpt);
      chosen[i] = chosen[i] === opt.dataset.qVal ? null : opt.dataset.qVal!;
      render();
      return;
    }
    if (t.closest('[data-q-add]')) {
      const v = variantFor(p, chosen);
      if (v?.available) {
        ($('#quick') as HTMLDialogElement).close();
        addToCart(v.id, 1, p.id, undefined, source);
        track('add_to_cart', [gaItem(p, v, 1)]);
      }
    }
  };
  render();
  const title = $('#quick-title');
  if (title) title.textContent = `Add ${p.name}`;
  openDialog('quick');
});

/* ---------------- cart ---------------- */

let quoteAbort: AbortController | null = null;
let lastQuote: Quote | null = null;

/** The cart's quote; 'busy' when this address has used up its allowance for now, 'stale' when a newer ask replaced this one. */
async function getQuote(): Promise<Quote | 'busy' | 'stale' | null> {
  quoteAbort?.abort();
  const mine = (quoteAbort = new AbortController());
  try {
    const res = await fetch('/api/cart/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cart.items, shipping: cart.shipping, code: cart.code || undefined }),
      signal: mine.signal,
    });
    if (res.status === 429) return 'busy';
    if (!res.ok) return null;
    return (await res.json()) as Quote;
  } catch {
    return mine.signal.aborted ? 'stale' : null;
  }
}
let busyTries = 0;

function walkHtml(q: Quote | null, compact: boolean): string {
  if (!q || !q.lines.length) return '';
  if (q.shipping === 'pickup') return '';
  const remaining = q.freeShippingRemainingCents;
  const p = siteCfg.free > 0 ? Math.min(1, 1 - remaining / siteCfg.free) : 1;
  const msg = remaining > 0
    ? `Bosco’s walking you to free shipping — <strong>${money(remaining)}</strong> to go`
    : 'Free shipping unlocked. Good walk!';
  return `<div class="walk${remaining > 0 ? '' : ' is-done'}" style="--p:${p.toFixed(3)}">
    <p class="walk__msg" ${compact ? '' : 'aria-live="polite"'}>${msg}</p>
    <div class="walk__track" aria-hidden="true"><div class="walk__lead"></div><span class="walk__dog">${BOSCO_SVG}</span>
    <svg class="walk__goal" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7.4 4.2a2.6 2.6 0 0 0-2.3 3.8 2.6 2.6 0 1 0 3.3 3.5l4.9 4.9a2.6 2.6 0 1 0 3.5 3.3 2.6 2.6 0 1 0 3.8-2.3 2.6 2.6 0 0 0-1.6-.5l-5-5a2.6 2.6 0 0 0-.4-3.4 2.6 2.6 0 0 0-3.4-.4 2.6 2.6 0 0 0-2.8-3.9z"/></svg></div></div>`;
}

/** The product's palette as inline custom properties, so a line or tile sits on its own colour. */
const palVars = (p: ApiProduct | undefined) => (p?.palette ? ` style="--bg:${esc(p.palette.bg)};--deep:${esc(p.palette.deep)}"` : '');

/** The product floating on its own colour, for cart lines, upsells and the quick-add sheet; falls back to the photo. */
function tileHtml(p: ApiProduct | undefined, photo: string | null, size: number): string {
  if (p?.cutout) return `<span class="tile"${palVars(p)}><img src="${esc(cutoutAt(p.cutout.url, size <= 100 ? 300 : 600))}" alt="" width="${p.cutout.w}" height="${p.cutout.h}" loading="lazy" decoding="async" data-fly></span>`;
  const src = p?.image ?? photo;
  return `<span class="tile"${palVars(p)}>${src ? `<img class="tile__photo" src="${esc(img(src, size * 2))}" alt="" width="${size}" height="${size}" loading="lazy" data-fly>` : ''}</span>`;
}

function linesHtml(q: Quote, byId: Map<string, ApiProduct>): string {
  return q.lines.map((l) => {
    const disc = l.discountCents > 0;
    const [base, print] = splitName(l.name);
    const p = byId.get(l.productId);
    return `<div class="line"${palVars(p)} data-line="${esc(l.v)}" data-note="${esc(l.note ?? '')}">
      ${tileHtml(p, l.image, 84)}
      <div><a class="line__name" href="/products/${esc(l.slug)}">${esc(base)}</a>
        ${print ? `<div class="line__print">${esc(print)}</div>` : ''}
        ${l.variantName && l.variantName !== 'Regular' ? `<div class="line__variant">${esc(l.variantName)}</div>` : ''}
        ${l.note ? `<div class="line__note">${esc(l.note)}</div>` : ''}
        <div class="line__ctrl"><div class="qty"><button type="button" data-line-qty="-1" aria-label="One less">−</button><input type="number" inputmode="numeric" min="0" max="${l.maxQty ?? 50}" value="${l.qty}" aria-label="Quantity of ${esc(l.name)}" data-line-input><button type="button" data-line-qty="1" aria-label="One more" ${l.maxQty !== null && l.qty >= l.maxQty ? 'disabled' : ''}>+</button></div>
        <button class="linkish" type="button" data-line-remove>Remove</button></div>
        ${l.maxQty !== null && l.maxQty <= 3 ? `<div class="line__variant" style="color:var(--orange-ink);font-weight:700">Only ${l.maxQty} left</div>` : ''}
      </div>
      <div class="line__price">${disc ? `<s>${money(l.unitCents * l.qty)}</s>` : ''}${money(l.lineCents)}</div></div>`;
  }).join('');
}

function optionsHtml(q: Quote): string {
  const ship = [
    `<label class="ship-opt"><input type="radio" name="ship" value="standard" ${q.shipping === 'standard' ? 'checked' : ''}><span>Standard post<small>Australia Post, 2–10 business days</small></span><strong>${q.shipping === 'standard' ? (q.shippingCents ? money(q.shippingCents) : 'Free') : money(q.itemsCents >= siteCfg.free ? 0 : siteCfg.standard) || 'Free'}</strong></label>`,
    siteCfg.express > 0 ? `<label class="ship-opt"><input type="radio" name="ship" value="express" ${q.shipping === 'express' ? 'checked' : ''}><span>Express post<small>Australia Post, 1–5 business days</small></span><strong>${money(siteCfg.express)}</strong></label>` : '',
    siteCfg.pickup ? `<label class="ship-opt"><input type="radio" name="ship" value="pickup" ${q.shipping === 'pickup' ? 'checked' : ''}><span>Click & Collect<small>Barnsley, NSW — we’ll let you know when it’s ready</small></span><strong>Free</strong></label>` : '',
  ].join('');
  const pickupFields = q.shipping === 'pickup'
    ? `<div class="personal"><div class="field"><label for="pu-name">Name for pickup</label><input class="input" id="pu-name" autocomplete="name" maxlength="60" value="${esc(cart.pickup.name)}" data-pickup="name" required></div>
       <div class="field"><label for="pu-phone">Mobile (we’ll text when it’s ready)</label><input class="input" id="pu-phone" type="tel" autocomplete="tel" inputmode="tel" maxlength="20" value="${esc(cart.pickup.phone)}" data-pickup="phone" required></div></div>`
    : '';
  const code = q.code
    ? `<p class="small">${q.code.applied ? `Code <strong>${esc(q.code.code)}</strong> applied (${esc(q.code.label)}).` : `<strong>${esc(q.code.code)}</strong> ${q.code.label ? 'doesn’t apply to these items.' : 'isn’t a current code.'}`} <button class="linkish" type="button" data-code-remove>Remove</button></p>`
    : `<details><summary class="small" style="cursor:pointer;font-weight:700">Have a code?</summary><form class="code-form" data-code-form style="margin-top:8px"><label class="sr" for="code-in">Discount code</label><input class="input" id="code-in" name="code" autocomplete="off" maxlength="24" style="text-transform:uppercase"><button class="btn btn--navy btn--sm" type="submit">Apply</button></form></details>`;
  // Email is optional (Square asks for it at checkout anyway); the marketing box starts unticked: ticking it is the consent.
  const contact = `<div class="contact"><div class="field"><label for="cart-email">Email for your receipt <span class="muted">(optional)</span></label><input class="input" id="cart-email" type="email" inputmode="email" autocomplete="email" maxlength="254" value="${esc(cart.email)}" placeholder="you@example.com" data-cart-email></div>
    <label class="acct-check"><input type="checkbox" data-cart-marketing ${cart.marketing ? 'checked' : ''}> Email me new prints and sales <span class="muted">(we’ll email you to confirm)</span></label></div>`;
  return `<fieldset class="ship-opts" style="border:0;padding:0;margin:16px 0 0"><legend class="label" style="margin-bottom:8px">Delivery</legend>${ship}</fieldset>${pickupFields}${contact}<div style="margin-top:12px">${code}</div>`;
}

function totalsHtml(q: Quote): string {
  return `<div class="totals">
      <div><span>Items</span><span>${money(q.subtotalCents)}</span></div>
      ${q.discountCents ? `<div class="save"><span>You save</span><span>−${money(q.discountCents)}</span></div>` : ''}
      <div><span>${q.shipping === 'pickup' ? 'Pickup' : 'Shipping'}</span><span>${q.shippingCents ? money(q.shippingCents) : 'Free'}</span></div>
      <div class="grand"><span>Total</span><span>${money(q.totalCents)}</span></div>
    </div>
    <button class="btn btn--block" type="button" data-checkout>Checkout securely</button>
    <p class="secure"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 10V7a6 6 0 0 1 12 0v3"/><path d="M5 10h14v11H5z"/></svg> Paid securely with Square${siteCfg.afterpay ? ' · Apple Pay · Afterpay' : ' · Apple Pay'}</p>`;
}

function problemsHtml(q: Quote): string {
  return q.problems.length ? `<div class="problems" role="alert">${q.problems.map((p) => `<p>${esc(p.message)}</p>`).join('')}</div>` : '';
}

async function upsells(q: Quote): Promise<string> {
  try {
    const res = await fetch(`/api/matching?ids=${q.lines.map((l) => encodeURIComponent(l.productId)).join(',')}`);
    if (!res.ok) return '';
    const data = (await res.json()) as { products: { id: string; slug: string; name: string; price: number; image: string | null }[] };
    if (!data.products.length) return '';
    const byId = new Map((await fetchProducts(data.products.map((p) => p.id))).map((p) => [p.id, p]));
    return `<section class="set" aria-label="Complete the set"><h3 class="set__title">Complete the set</h3><div class="set__items">${data.products.map((p) => {
      const [base, print] = splitName(p.name);
      return `<div class="set__item"${palVars(byId.get(p.id))}><a class="set__media" href="/products/${esc(p.slug)}" tabindex="-1" aria-hidden="true">${tileHtml(byId.get(p.id), p.image, 120)}</a><a class="set__name" href="/products/${esc(p.slug)}">${esc(base)}</a>${print ? `<span class="line__print">${esc(print)}</span>` : ''}<span class="set__row"><span class="price">${money(p.price)}</span><button type="button" class="btn btn--navy btn--sm" data-quick-add="${esc(p.id)}" aria-label="Add ${esc(p.name)}">Add</button></span></div>`;
    }).join('')}</div></section>`;
  } catch {
    return '';
  }
}

function applyProblems(q: Quote) {
  // Drop lines Square says are gone; clamp quantities to live stock.
  const valid = new Map(q.lines.map((l) => [l.v, l]));
  const before = JSON.stringify(cart.items);
  cart.items = cart.items
    .filter((i) => valid.has(i.v))
    .map((i) => ({ ...i, q: Math.min(i.q, valid.get(i.v)!.maxQty ?? i.q) }))
    .filter((i) => i.q > 0);
  if (JSON.stringify(cart.items) !== before) saveCart();
}

function renderAnnWalk(q: Quote | null) {
  const walk = $('[data-ann-walk]');
  const text = $('[data-ann-text]');
  if (!walk || !text) return;
  const html = walkHtml(q, true);
  if (html) {
    walk.innerHTML = html;
    walk.hidden = false;
    text.hidden = true;
  } else {
    walk.hidden = true;
    text.hidden = false;
  }
}

async function refreshCart(trot = false) {
  const body = $('[data-cart-body]');
  const foot = $('[data-cart-foot]');
  const pageLines = $('[data-cart-lines]');
  const pageSide = $('[data-cart-side]');
  if (!cart.items.length) {
    lastQuote = null;
    const empty = `<div class="empty empty--sit"><span class="empty__dog" aria-hidden="true">${BOSCO_SVG.replace('class="', 'class="bosco ')}</span><h2>Bosco’s bowl is empty</h2><p class="muted">Nothing in your cart yet.</p><a class="btn" href="/collections/all">Start shopping</a></div>`;
    if (body) body.innerHTML = empty;
    if (foot) foot.hidden = true;
    if (pageLines) pageLines.innerHTML = empty;
    if (pageSide) pageSide.hidden = true;
    renderAnnWalk(null);
    renderCounts();
    return;
  }
  const q = await getQuote();
  if (q === 'stale') return; // a newer refresh is on its way
  if (q === 'busy') {
    // Everyone behind one address shares an allowance, and it's used up for now: the cart stays as last shown
    // and asks again after 5, 25 and 65 seconds (the last past any minute's allowance) before saying to come back.
    const again = busyTries < 3;
    if (again) window.setTimeout(() => refreshCart(), [5000, 20000, 40000][busyTries++]);
    if (again && lastQuote) return;
    const msg = `<p class="problems" role="status">The shop’s very busy right now. Your cart is saved${again ? ' — trying again in a moment…' : '. Please try again in a minute.'}</p>`;
    if (body) body.innerHTML = msg;
    if (pageLines) pageLines.innerHTML = msg;
    return;
  }
  busyTries = 0;
  if (!q) {
    const msg = '<p class="problems" role="alert">We couldn’t load your cart. Check your connection and try again.</p>';
    if (body) body.innerHTML = msg;
    if (pageLines) pageLines.innerHTML = msg;
    return;
  }
  lastQuote = q;
  applyProblems(q);
  // Cutouts and palettes for the lines come from the product API (cached); the quote itself stays lean.
  const byId = new Map((await fetchProducts(q.lines.map((l) => l.productId))).map((p) => [p.id, p]));
  const lines = problemsHtml(q) + walkHtml(q, false) + linesHtml(q, byId);
  // Drawer: items, upsell and options scroll; only the total + button stay pinned.
  if (body) body.innerHTML = `${lines}<div data-upsell></div>${optionsHtml(q)}`;
  if (foot) {
    foot.innerHTML = totalsHtml(q);
    foot.hidden = false;
  }
  if (pageLines) pageLines.innerHTML = `${lines}<div data-upsell></div>`;
  if (pageSide) {
    pageSide.innerHTML = optionsHtml(q) + totalsHtml(q);
    pageSide.hidden = false;
  }
  renderAnnWalk(q);
  if (trot && !reduceMotion) for (const d of $$('.walk__dog')) d.classList.add('is-trotting');
  const extra = await upsells(q);
  if (extra) for (const slot of $$('[data-upsell]')) slot.innerHTML = extra;
}

let refreshTimer = 0;
const refreshSoon = () => {
  clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => refreshCart(), 250);
};

function lineKey(el: HTMLElement) {
  const line = el.closest<HTMLElement>('[data-line]')!;
  return { v: line.dataset.line!, note: line.dataset.note ?? '' };
}
function setQty(v: string, note: string, q: number) {
  const item = cart.items.find((i) => i.v === v && (i.note ?? '') === note);
  if (!item) return;
  item.q = Math.max(0, Math.min(50, q));
  cart.items = cart.items.filter((i) => i.q > 0);
  saveCart();
  refreshSoon();
}

document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  const step = t.closest<HTMLButtonElement>('[data-line-qty]');
  if (step) {
    const { v, note } = lineKey(step);
    const item = cart.items.find((i) => i.v === v && (i.note ?? '') === note);
    if (item) setQty(v, note, item.q + Number(step.dataset.lineQty));
    return;
  }
  const rm = t.closest<HTMLButtonElement>('[data-line-remove]');
  if (rm) {
    const { v, note } = lineKey(rm);
    setQty(v, note, 0);
    return;
  }
  if (t.closest('[data-code-remove]')) {
    cart.code = '';
    saveCart();
    refreshSoon();
    return;
  }
  const co = t.closest<HTMLButtonElement>('[data-checkout]');
  if (co) checkout(co);
});
document.addEventListener('change', (e) => {
  const t = e.target as HTMLInputElement;
  if (t.matches('[data-line-input]')) {
    const { v, note } = lineKey(t);
    setQty(v, note, Number.parseInt(t.value, 10) || 0);
  }
  if (t.name === 'ship') {
    cart.shipping = t.value as CartState['shipping'];
    saveCart();
    refreshSoon();
  }
  if (t.matches('[data-cart-marketing]')) {
    cart.marketing = t.checked;
    LS.set(CART_KEY, cart);
  }
});
document.addEventListener('input', (e) => {
  const t = e.target as HTMLInputElement;
  if (t.dataset.pickup) {
    cart.pickup = { ...cart.pickup, [t.dataset.pickup]: t.value };
    LS.set(CART_KEY, cart);
  }
  if (t.matches('[data-cart-email]')) {
    cart.email = t.value.trim().slice(0, 254);
    LS.set(CART_KEY, cart);
  }
});
document.addEventListener('submit', (e) => {
  const f = e.target as HTMLFormElement;
  if (f.matches('[data-code-form]')) {
    e.preventDefault();
    cart.code = String(new FormData(f).get('code') ?? '').trim().toUpperCase();
    saveCart();
    refreshSoon();
  }
});

const EMAIL_OK = /^[^\s@]{1,64}@[^\s@]{1,190}\.[A-Za-z]{2,24}$/;

async function checkout(btn: HTMLButtonElement) {
  if (cart.shipping === 'pickup' && (!cart.pickup.name.trim() || cart.pickup.phone.replace(/\D/g, '').length < 8)) {
    toast('Add a name and mobile for Click & Collect');
    ($('[data-pickup="name"]') as HTMLInputElement | null)?.focus();
    return;
  }
  if (cart.email && !EMAIL_OK.test(cart.email)) {
    toast('That email doesn’t look right');
    ($('[data-cart-email]') as HTMLInputElement | null)?.focus();
    return;
  }
  if (cart.marketing && !cart.email && !me?.email) {
    toast('Add your email so we know where to send the news');
    ($('[data-cart-email]') as HTMLInputElement | null)?.focus();
    return;
  }
  btn.disabled = true;
  const label = btn.textContent;
  btn.textContent = 'Opening secure checkout…';
  try {
    const res = await fetch('/api/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cart.items, shipping: cart.shipping, code: cart.code || undefined, pickup: cart.pickup, email: cart.email || undefined, marketing: cart.marketing }),
    });
    const data = (await res.json()) as { url?: string; orderId?: string; ref?: string; problems?: { message: string }[]; error?: string };
    if (res.ok && data.url) {
      try {
        sessionStorage.setItem('bosco.lastOrder', JSON.stringify({ orderId: data.orderId, ref: data.ref, at: Date.now() }));
      } catch {
        /* ignore */
      }
      const q = lastQuote;
      const items = (q?.lines ?? []).map((l): GaItem => ({ item_id: l.v, item_name: l.name, ...(l.variantName ? { item_variant: l.variantName } : {}), price: dollars(l.lineCents / l.qty), quantity: l.qty }));
      if (cart.marketing) gtag('event', 'generate_lead', { lead_source: 'checkout' });
      trackThenGo('begin_checkout', items, { ...(q ? { value: dollars(q.itemsCents) } : {}), ...(q?.code?.applied ? { coupon: q.code.code } : {}) }, data.url);
      return;
    }
    if (data.problems?.length) {
      toast(esc(data.problems[0].message));
      await refreshCart();
    } else {
      toast(esc(data.error ?? 'Checkout didn’t start. Please try again.'));
    }
  } catch {
    toast('Checkout didn’t start — check your connection and try again.');
  }
  btn.disabled = false;
  btn.textContent = label;
}

$('#cart')?.addEventListener('bosco:open', () => refreshCart());

/* ---------------- product page ---------------- */

interface ProductData { id: string; slug: string; name: string; group: string; image: string | null; options: { name: string; values: string[] }[]; personalised: boolean; pending?: boolean; variants: ApiVariant[]; images: string[] }

function initProduct() {
  const root = $('[data-product]');
  const dataEl = $('#product-data');
  if (!root || !dataEl) return;
  const p = JSON.parse(dataEl.textContent || '{}') as ProductData;
  const form = $<HTMLFormElement>('[data-buy-form]', root)!;
  const addBtn = $<HTMLButtonElement>('[data-add]', root)!;
  const priceEl = $('[data-price]', root)!;
  const stockEl = $('[data-stock-note]', root)!;
  const afterpay = $('[data-afterpay]', root);
  const qtyInput = $<HTMLInputElement>('#qty', root)!;
  const chosen: (string | null)[] = p.options.map((o) => (o.values.length === 1 ? o.values[0] : null));

  // Deep link (?variant=) or the shopper's pet profile size pre-selects options.
  const params = new URLSearchParams(location.search);
  const deep = p.variants.find((v) => v.id === params.get('variant'));
  if (deep) deep.values.forEach((val, i) => (chosen[i] = val));
  const sizeIdx = p.options.findIndex((o) => /size/i.test(o.name));
  if (!deep && sizeIdx >= 0 && profile.size && (!profile.pet || profile.pet === p.group)) {
    const fit = p.options[sizeIdx].values.find((val) => sizeKey(val) === profile.size && valueAvailable(p, chosen, sizeIdx, val));
    if (fit) chosen[sizeIdx] = fit;
  }
  const viewed = variantFor(p, chosen) ?? p.variants.find((x) => x.available) ?? p.variants[0];
  if (viewed) track('view_item', [gaItem(p, viewed, 1)]);

  const render = () => {
    const v = variantFor(p, chosen);
    $$<HTMLFieldSetElement>('[data-option]', root).forEach((fs) => {
      const i = Number(fs.dataset.option);
      const label = $('[data-option-value]', fs);
      if (label) label.textContent = chosen[i] ?? '';
      $$<HTMLButtonElement>('[data-value]', fs).forEach((b) => {
        b.setAttribute('aria-pressed', String(chosen[i] === b.dataset.value));
        // While it's being counted no option is for sale, which isn't "sold out": the options stay plain.
        b.dataset.unavailable = String(!p.pending && !valueAvailable(p, chosen, i, b.dataset.value!));
        if (i === sizeIdx && profile.size) b.dataset.fits = String(sizeKey(b.dataset.value!) === profile.size);
      });
    });
    if (v) {
      priceEl.innerHTML = `<span class="price">${v.sale !== null ? `<s><span class="sr">Was </span>${money(v.price)}</s><strong class="now"><span class="sr">Now </span>${money(v.sale)}</strong>` : money(v.price)}</span>`;
      if (afterpay) afterpay.textContent = `or 4 interest-free payments of ${money(Math.ceil((v.sale ?? v.price) / 4))} with Afterpay`;
      const low = v.stock !== null && v.stock <= 3;
      // "In this option" only where there are others to choose from.
      const here = p.variants.length > 1 ? ' in this option' : '';
      stockEl.className = `stock stock--${!v.available ? 'out' : low ? 'low' : 'ok'}`;
      stockEl.textContent = !v.available ? (p.pending ? 'Back online soon' : `Sold out${here}`) : low ? (v.stock === 1 ? `Last one${here}` : `Only ${v.stock} left`) : 'In stock';
      addBtn.disabled = !v.available;
      addBtn.textContent = v.available ? 'Add to cart' : p.pending ? 'Back online soon' : 'Sold out';
      // Quantity stays at least 1 and at most what's left; there's nothing to choose while it's sold out.
      const cap = v.available ? Math.max(1, v.stock ?? 50) : 1;
      qtyInput.max = String(cap);
      qtyInput.value = String(Math.min(cap, Math.max(1, Number(qtyInput.value) || 1)));
      for (const el of $$<HTMLButtonElement | HTMLInputElement>('.qty button, .qty input', root)) el.disabled = !v.available;
      const u = new URL(location.href);
      if (p.variants.length > 1) u.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', u);
      if (v.image) {
        const idx = p.images.indexOf(v.image);
        if (idx >= 0) showImage(idx);
      }
    } else {
      const missing = p.options[chosen.findIndex((c) => c === null)];
      addBtn.disabled = !p.variants.some((x) => x.available);
      addBtn.textContent = p.pending ? 'Back online soon' : missing ? `Choose a ${missing.name.toLowerCase()}` : 'Sold out';
    }
    syncSticky();
  };

  root.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-value]');
    if (!b) return;
    const i = Number(b.closest<HTMLElement>('[data-option]')!.dataset.option);
    chosen[i] = chosen[i] === b.dataset.value ? null : b.dataset.value!;
    render();
  });
  root.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-qty]');
    if (!b) return;
    const next = (Number(qtyInput.value) || 1) + Number(b.dataset.qty);
    qtyInput.value = String(Math.max(1, Math.min(Number(qtyInput.max) || 50, next)));
  });

  const add = () => {
    const v = variantFor(p, chosen);
    if (!v) {
      const first = $('[data-option] .chip', root);
      first?.focus();
      toast(`Choose a ${p.options[chosen.findIndex((c) => c === null)]?.name.toLowerCase() ?? 'option'} first`);
      return;
    }
    let note: string | undefined;
    if (p.personalised) {
      const name = ($<HTMLInputElement>('[name="petName"]', form)?.value ?? '').trim();
      if (!name) {
        $<HTMLInputElement>('[name="petName"]', form)?.focus();
        toast('Add your pet’s name for the personalisation');
        return;
      }
      const phone = ($<HTMLInputElement>('[name="petPhone"]', form)?.value ?? '').trim();
      const extra = ($<HTMLInputElement>('[name="petNotes"]', form)?.value ?? '').trim();
      note = [`Name: ${name}`, phone && `Phone: ${phone}`, extra && `Notes: ${extra}`].filter(Boolean).join(' · ');
    }
    const qty = Math.max(1, Number(qtyInput.value) || 1);
    addToCart(v.id, qty, p.id, note, $('[data-gal-main] [data-fly]', root));
    track('add_to_cart', [gaItem(p, v, qty)]);
    addBtn.classList.add('is-done');
    addBtn.textContent = 'Added';
    setTimeout(() => {
      addBtn.classList.remove('is-done');
      render();
    }, 1600);
  };
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    add();
  });

  // Gallery
  const main = $('[data-gal-main]', root);
  const thumbs = $$<HTMLButtonElement>('[data-thumb]', root);
  function showImage(i: number) {
    const slide = main?.children[i] as HTMLElement | undefined;
    slide?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
  }
  thumbs.forEach((t) => t.addEventListener('click', () => showImage(Number(t.dataset.thumb))));
  if (main && thumbs.length) {
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) if (en.isIntersecting) {
        const i = [...main.children].indexOf(en.target);
        thumbs.forEach((t, j) => t.setAttribute('aria-current', String(i === j)));
      }
    }, { root: main, threshold: 0.6 });
    [...main.children].forEach((c) => io.observe(c));
  }
  // Lightbox
  root.addEventListener('click', (e) => {
    const z = (e.target as HTMLElement).closest<HTMLElement>('[data-zoom]');
    if (!z) return;
    openDialog('lightbox');
    const track = $('[data-lightbox-track]');
    const target = track?.children[Number(z.dataset.zoom)] as HTMLElement | undefined;
    requestAnimationFrame(() => target?.scrollIntoView({ block: 'nearest', inline: 'center' }));
  });

  // Share
  $('[data-share]', root)?.addEventListener('click', async () => {
    const data = { title: p.name, text: `${p.name} from Bosco & Co`, url: location.href };
    try {
      if (navigator.share) await navigator.share(data);
      else {
        await navigator.clipboard.writeText(location.href);
        toast('Link copied');
      }
    } catch {
      /* share sheet dismissed */
    }
  });

  // Sticky add-to-cart bar once the main button scrolls away (mobile).
  const sticky = $('[data-sticky-buy]');
  const stickyBtn = $<HTMLButtonElement>('[data-sticky-add]');
  function syncSticky() {
    if (!stickyBtn) return;
    stickyBtn.disabled = addBtn.disabled && !addBtn.textContent?.startsWith('Choose');
    stickyBtn.textContent = addBtn.textContent?.startsWith('Choose') ? 'Choose' : addBtn.disabled ? (addBtn.textContent === 'Back online soon' ? 'Back soon' : 'Sold out') : 'Add';
  }
  stickyBtn?.addEventListener('click', () => {
    if (addBtn.textContent?.startsWith('Choose')) {
      $('[data-option]', root)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    add();
  });
  if (sticky) {
    new IntersectionObserver(([en]) => {
      const show = !en.isIntersecting && en.boundingClientRect.top < 0;
      sticky.classList.toggle('is-shown', show);
      sticky.setAttribute('aria-hidden', String(!show));
      if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
    }).observe(addBtn);
  }

  // Remember for "You looked at".
  recent = [p.id, ...recent.filter((x) => x !== p.id)].slice(0, 12);
  LS.set('bosco.recent.v1', recent);
  render();
}

/* ---------------- pet profile ---------------- */

function sizeKey(v: string): string | null {
  const s = v.trim();
  const table: [RegExp, string][] = [[/^(xxs)$/i, 'XXS'], [/^(xs|x[\s-]?small|extra[\s-]?small)\b/i, 'XS'], [/^(s|small)\b/i, 'S'], [/^(m|medium)\b/i, 'M'], [/^(l|large)\b/i, 'L'], [/^(xl|x[\s-]?large|extra[\s-]?large)\b/i, 'XL'], [/^(xxl|2xl)\b/i, 'XXL']];
  for (const [re, k] of table) if (re.test(s)) return k;
  return null;
}

function renderProfile() {
  for (const b of $$<HTMLButtonElement>('[data-pet]')) b.setAttribute('aria-pressed', String(profile.pet === b.dataset.pet));
  for (const b of $$<HTMLButtonElement>('[data-size]')) b.setAttribute('aria-pressed', String(profile.size === b.dataset.size));
  const clear = $('[data-profile-clear]');
  if (clear) clear.hidden = !profile.pet && !profile.size;
  for (const card of $$('[data-card]')) {
    const badge = $('[data-fit-badge]', card);
    if (!badge) continue;
    const sizes = (card.dataset.sizes ?? '').split(' ').filter(Boolean);
    const petOk = !profile.pet || card.dataset.group === profile.pet;
    badge.hidden = !(profile.size && petOk && sizes.includes(profile.size));
  }
}
document.addEventListener('click', (e) => {
  const t = e.target as HTMLElement;
  const pet = t.closest<HTMLButtonElement>('[data-pet]');
  const size = t.closest<HTMLButtonElement>('[data-size]');
  if (pet) profile.pet = profile.pet === pet.dataset.pet ? '' : (pet.dataset.pet as Pet);
  else if (size) profile.size = profile.size === size.dataset.size ? '' : size.dataset.size!;
  else if (t.closest('[data-profile-clear]')) profile = { pet: '', size: '' };
  else return;
  LS.set('bosco.pet.v1', profile);
  renderProfile();
  syncAccount();
  if (profile.pet && pet) {
    toast(`Showing what fits your ${profile.pet}${profile.size ? ` (${profile.size})` : ''}. <a href="/collections/${profile.pet}">Shop ${profile.pet}</a>`);
  }
});

/* ---------------- collection filters ---------------- */

function initFilters() {
  const bar = $('[data-filters]');
  const grid = $('[data-grid]');
  if (!bar || !grid) return;
  const cards = $$('[data-card]', grid);
  const original = [...cards];
  const sort = $<HTMLSelectElement>('[data-sort]', bar)!;
  const params = new URLSearchParams(location.search);
  const state = { sort: params.get('sort') ?? 'featured', size: params.get('size') ?? '', price: Number(params.get('under') ?? 0), sale: params.get('sale') === '1' };
  sort.value = state.sort;
  const apply = () => {
    let shown = 0;
    for (const c of cards) {
      const ok = (!state.size || (c.dataset.sizes ?? '').split(' ').includes(state.size))
        && (!state.price || Number(c.dataset.price) < state.price)
        && (!state.sale || c.dataset.sale === '1');
      c.hidden = !ok;
      if (ok) shown++;
    }
    const by: Record<string, (a: HTMLElement, b: HTMLElement) => number> = {
      low: (a, b) => Number(a.dataset.price) - Number(b.dataset.price),
      high: (a, b) => Number(b.dataset.price) - Number(a.dataset.price),
      new: (a, b) => (b.dataset.new ?? '').localeCompare(a.dataset.new ?? ''),
      few: (a, b) => (Number(a.dataset.stock || 999) - Number(b.dataset.stock || 999)),
    };
    const ordered = state.sort === 'featured' ? original : [...cards].sort(by[state.sort] ?? (() => 0));
    for (const c of ordered) grid.appendChild(c);
    const count = $('[data-result-count]');
    if (count) count.textContent = `${shown} ${shown === 1 ? 'product' : 'products'}`;
    const empty = $('[data-filter-empty]');
    if (empty) empty.hidden = shown > 0;
    for (const b of $$<HTMLButtonElement>('[data-filter-size]', bar)) b.setAttribute('aria-pressed', String(b.dataset.filterSize === state.size));
    for (const b of $$<HTMLButtonElement>('[data-filter-price]', bar)) b.setAttribute('aria-pressed', String(Number(b.dataset.filterPrice) === state.price));
    $$<HTMLButtonElement>('[data-filter-sale]', bar).forEach((b) => b.setAttribute('aria-pressed', String(state.sale)));
    const u = new URL(location.href);
    u.searchParams.delete('sort'); u.searchParams.delete('size'); u.searchParams.delete('under'); u.searchParams.delete('sale');
    if (state.sort !== 'featured') u.searchParams.set('sort', state.sort);
    if (state.size) u.searchParams.set('size', state.size);
    if (state.price) u.searchParams.set('under', String(state.price));
    if (state.sale) u.searchParams.set('sale', '1');
    history.replaceState(history.state, '', u);
  };
  sort.addEventListener('change', () => {
    state.sort = sort.value;
    apply();
  });
  bar.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const s = t.closest<HTMLButtonElement>('[data-filter-size]');
    const p = t.closest<HTMLButtonElement>('[data-filter-price]');
    if (s) state.size = state.size === s.dataset.filterSize ? '' : s.dataset.filterSize!;
    else if (p) state.price = state.price === Number(p.dataset.filterPrice) ? 0 : Number(p.dataset.filterPrice);
    else if (t.closest('[data-filter-sale]')) state.sale = !state.sale;
    else return;
    apply();
  });
  // A pet profile size pre-filters sized collections, once, if nothing else is set.
  if (!params.toString() && profile.size && $(`[data-filter-size="${profile.size}"]`, bar)) state.size = profile.size;
  apply();
}

/* ---------------- search ---------------- */

interface Doc { id: string; n: string; s: string; p: number; w: number | null; i: string; cu: string; b: string; c: string; k: string; o: boolean; r?: true }
// The shop's search ranks what's typed (/api/search, cached per catalogue version); answers already fetched are kept.
const suggested = new Map<string, Promise<Doc[]>>();
function searchDocs(q: string): Promise<Doc[]> {
  const key = q.toLowerCase();
  let found = suggested.get(key);
  if (!found) {
    found = fetch(`/api/search?q=${encodeURIComponent(q)}&v=${encodeURIComponent(siteCfg.v)}`)
      .then((res) => (res.ok ? (res.json() as Promise<Doc[]>) : []))
      .catch(() => {
        suggested.delete(key);
        return [];
      });
    suggested.set(key, found);
  }
  return found;
}
function initSearch() {
  const dialog = $<HTMLDialogElement>('#search');
  const input = $<HTMLInputElement>('[data-search-input]');
  const out = $('[data-search-results]');
  if (!dialog || !input || !out) return;
  let active = -1;
  let hits: Doc[] = [];
  let typed = 0; // the latest input: an older answer arriving late is dropped
  dialog.addEventListener('bosco:open', () => {
    input.value = '';
    out.innerHTML = '';
    input.focus();
  });
  const highlight = (text: string, q: string) => {
    let html = esc(text);
    for (const t of q.toLowerCase().split(/\s+/).filter((x) => x.length > 1)) {
      html = html.replace(new RegExp(`(^|\\s)(${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'), '$1<mark>$2</mark>');
    }
    return html;
  };
  const render = async () => {
    const q = input.value.trim();
    const mine = ++typed;
    const found = q.length >= 2 ? await searchDocs(q) : [];
    if (mine !== typed) return;
    hits = found;
    active = -1;
    input.setAttribute('aria-expanded', String(hits.length > 0));
    out.innerHTML = q.length < 2
      ? '<p class="search__empty">Try “harness”, “bandana”, “galaxy” or “treats”.</p>'
      : hits.length
        ? hits.map((d, i) => `<a class="search__hit" role="option" id="hit-${i}" aria-selected="false" href="/products/${esc(d.s)}"><span class="tile tile--sm" style="--bg:${esc(d.b || '#f2f5fb')}">${d.cu ? `<img src="${esc(d.cu)}" alt="" width="56" height="56" loading="lazy" decoding="async">` : d.i ? `<img class="tile__photo" src="${esc(d.i)}" alt="" width="56" height="56" loading="lazy">` : ''}</span><span><strong>${highlight(d.n, q)}</strong><br><span class="small muted">${esc(d.c)}${d.o ? '' : d.r ? ' · back soon' : ' · sold out'}</span></span><span class="price">${d.w ? `<s>${money(d.w)}</s> ` : ''}${money(d.p)}</span></a>`).join('') + `<a class="search__hit search__hit--all" href="/search?q=${encodeURIComponent(q)}"><strong>See all results for “${esc(q)}”</strong></a>`
        : `<p class="search__empty">No sniffs for “${esc(q)}”. Try a simpler word.</p>`;
  };
  input.addEventListener('input', render);
  input.addEventListener('keydown', (e) => {
    const links = $$<HTMLAnchorElement>('.search__hit', out);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!links.length) return;
      active = (active + (e.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
      links.forEach((l, i) => l.setAttribute('aria-selected', String(i === active)));
      input.setAttribute('aria-activedescendant', links[active].id || '');
      links[active].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && active >= 0 && links[active]) {
      e.preventDefault();
      location.href = links[active].href;
    }
  });
  addEventListener('keydown', (e) => {
    if (e.key === '/' && !(e.target as HTMLElement).closest('input, textarea, select, [contenteditable]')) {
      e.preventDefault();
      openDialog('search');
    }
  });
}

/* ---------------- favourites page & recently viewed ---------------- */

/** The favourites grid (the favourites page and the account page) from the current list. */
async function renderFavGrid() {
  const favGrid = $('[data-favourites]');
  if (!favGrid) return;
  const list = await fetchProducts(favs);
  favGrid.innerHTML = list.map(cardHtml).join('');
  const empty = $('[data-favourites-empty]');
  if (empty) empty.hidden = list.length > 0;
  renderFavs();
}

async function initLists() {
  await renderFavGrid();
  const recentRail = $('[data-recent]');
  const recentSection = $('[data-recent-section]');
  if (recentRail && recentSection && recent.length >= 2) {
    const list = (await fetchProducts(recent.slice(0, 10))).filter((p) => p.inStock);
    if (list.length >= 2) {
      recentRail.innerHTML = list.map((p) => `<div>${cardHtml(p)}</div>`).join('');
      recentSection.hidden = false;
      renderFavs();
    }
  }
}

/* ---------------- order confirmation ---------------- */

/** GA4 purchase, sent once per order even if the thank-you page is reloaded. `value` excludes shipping. */
function trackPurchase(orderId: string, o: { lines: { name: string; qty: number; totalCents: number; v?: string; item?: string; variant?: string }[]; totalCents: number; shippingCents?: number }) {
  if (!gaId) return;
  const prev = LS.get<unknown>('bosco.ga.orders', []);
  const sent = Array.isArray(prev) ? prev.filter((x): x is string => typeof x === 'string') : [];
  if (sent.includes(orderId)) return;
  const items = o.lines
    .filter((l) => l.qty > 0)
    .map((l): GaItem => ({ ...(l.v ? { item_id: l.v } : {}), item_name: l.item ?? l.name, ...(l.variant ? { item_variant: l.variant } : {}), price: dollars(l.totalCents / l.qty), quantity: l.qty }));
  const shipping = o.shippingCents ?? 0;
  track('purchase', items, { transaction_id: orderId, value: dollars(o.totalCents - shipping), shipping: dollars(shipping) });
  LS.set('bosco.ga.orders', [orderId, ...sent].slice(0, 20));
}

async function initThanks() {
  const root = $('[data-thanks]');
  if (!root) return;
  const ref = new URLSearchParams(location.search).get('ref') ?? '';
  let last: { orderId?: string; ref?: string } = {};
  try {
    last = JSON.parse(sessionStorage.getItem('bosco.lastOrder') || '{}');
  } catch {
    /* ignore */
  }
  const show = (html: string) => (root.innerHTML = `<div class="empty empty--thanks" style="padding-top:24px"><span class="empty__dog" aria-hidden="true">${BOSCO_SVG.replace('class="', 'class="bosco ')}</span>${html}</div>`);
  if (!last.orderId || !ref || last.ref !== ref) {
    show('<h1 style="font-size:var(--step-4);font-stretch:80%">Thanks for your order!</h1><p>Square has emailed your receipt. We’ll let you know when it’s on its way.</p><a class="btn" href="/">Back to the shop</a>');
    cart = emptyCart();
    saveCart();
    return;
  }
  for (let attempt = 0; attempt < 8; attempt++) {
    try {
      const res = await fetch(`/api/order?id=${encodeURIComponent(last.orderId)}&ref=${encodeURIComponent(ref)}`);
      if (res.ok) {
        const o = (await res.json()) as { paid: boolean; lines: { name: string; qty: number; totalCents: number; v?: string; item?: string; variant?: string }[]; totalCents: number; shippingCents?: number; pickup: boolean };
        if (o.paid) {
          cart = emptyCart();
          saveCart();
          trackPurchase(last.orderId, o);
          show(`<h1 style="font-size:var(--step-4);font-stretch:80%">Thanks — order confirmed!</h1>
            <p>${o.pickup ? 'We’ll text you when it’s ready to collect from Barnsley.' : 'We’ll pack it by hand and let you know when it’s on its way.'} Your receipt is in your email from Square.</p>
            <div class="cart-page__side" style="text-align:left;width:min(480px,100%)"><div class="totals">${o.lines.map((l) => `<div><span>${l.qty} × ${esc(l.name)}</span><span>${money(l.totalCents)}</span></div>`).join('')}<div class="grand"><span>Total</span><span>${money(o.totalCents)}</span></div></div></div>
            <p class="muted">Tag <a href="https://instagram.com/boscoandco20">@boscoandco20</a> when it arrives — we love seeing pups in their new gear.</p>
            <a class="btn" href="/">Keep shopping</a>`);
          return;
        }
      }
    } catch {
      /* retry */
    }
    await new Promise((r) => setTimeout(r, 1500));
  }
  show('<h1 style="font-size:var(--step-3);font-stretch:80%">Payment not finished</h1><p>Your cart is still here if you’d like to try again.</p><button class="btn" type="button" data-open="cart">Open cart</button>');
}

/* ---------------- accounts: who's signed in; favourites and the pet profile kept in step ---------------- */

let me: { email: string; csrf: string } | null = null;
// Signing in sets a plain marker cookie beside the httpOnly session, so pages only ask who's in when someone is.
const signedIn = () => document.cookie.split(';').some((c) => c.trim().startsWith('bosco_in='));

async function whoAmI(): Promise<typeof me> {
  if (!signedIn()) return null;
  try {
    const res = await fetch('/account/api/me', { cache: 'no-store' });
    me = res.ok ? ((await res.json()) as typeof me) : null;
  } catch {
    me = null;
  }
  return me;
}

/** Browser favourites and pet profile merged into the account's (union, newest first), a moment after they change. */
let syncTimer = 0;
function syncAccount() {
  if (!me?.csrf) return;
  clearTimeout(syncTimer);
  syncTimer = window.setTimeout(async () => {
    try {
      const res = await fetch('/account/api/sync', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF': me!.csrf }, body: JSON.stringify({ favourites: favs, pet: profile }) });
      if (!res.ok) return;
      const merged = (await res.json()) as { favourites: string[]; pet: { pet?: Pet; size?: string } };
      const changed = JSON.stringify(merged.favourites) !== JSON.stringify(favs);
      favs = merged.favourites.filter((x) => typeof x === 'string').slice(0, 100);
      LS.set('bosco.fav.v1', favs);
      if (merged.pet.pet || merged.pet.size) {
        profile = { pet: merged.pet.pet ?? '', size: merged.pet.size ?? '' };
        LS.set('bosco.pet.v1', profile);
      }
      renderFavs();
      renderProfile();
      if (changed && $('[data-account]')) await renderFavGrid();
    } catch {
      /* offline: what's on this device stands */
    }
  }, 350);
}

async function initAccount() {
  const root = $('[data-account]');
  const who = await whoAmI();
  for (const a of $$('[data-account-link]')) {
    a.classList.toggle('is-in', !!who);
    if (who) a.setAttribute('aria-label', 'Your account (signed in)');
  }
  if (who && !cart.email) {
    cart.email = who.email;
    LS.set(CART_KEY, cart);
  }
  if (!root) return;
  // The page itself is proof of sign-in: its CSRF token does when the marker cookie is missing.
  me ??= { email: '', csrf: root.dataset.csrf ?? '' };
  syncAccount();
  // Just arrived from a sign-in link (a clean address; the token never reached the browser's
  // history): one analytics event, no personal data, then the flag comes off the address.
  const arrived = new URLSearchParams(location.search).get('f');
  if (arrived === 'in' || arrived === 'new') {
    gtag('event', arrived === 'new' ? 'sign_up' : 'login', { method: 'email' });
    history.replaceState(null, '', '/account');
  }
  root.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-buy-again]');
    if (!b) return;
    let lines: { v: string; q: number }[] = [];
    try {
      lines = JSON.parse(b.dataset.buyAgain ?? '[]') as { v: string; q: number }[];
    } catch {
      return;
    }
    for (const l of lines) if (typeof l.v === 'string' && Number.isInteger(l.q) && l.q > 0) putInCart(l.v, Math.min(50, l.q), '');
    saveCart();
    refreshCart(true);
    bump('[data-cart-count]');
    toast(`Bosco fetched ${lines.length} ${lines.length === 1 ? 'item' : 'items'} — anything sold out is left off`);
    openDialog('cart');
  });
}

/* ---------------- join the pack (email capture) ---------------- */

function initJoin() {
  const form = $<HTMLFormElement>('[data-join]');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = $<HTMLButtonElement>('button', form)!;
    const note = $('[data-join-note]', form);
    const email = ($<HTMLInputElement>('input[name="email"]', form)?.value ?? '').trim();
    const website = $<HTMLInputElement>('input[name="website"]', form)?.value ?? '';
    if (!EMAIL_OK.test(email)) {
      if (note) note.textContent = 'That email doesn’t look right.';
      return;
    }
    btn.disabled = true;
    try {
      const res = await fetch('/api/subscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, website }) });
      const data = (await res.json()) as { ok?: boolean; code?: string | null; emailed?: boolean; state?: 'pending' | 'already' | 'confirmed'; error?: string };
      if (res.ok && data.ok) {
        gtag('event', 'generate_lead', { lead_source: 'join_the_pack' });
        // Double opt-in: the code now, the newsletter once the address confirms from our email.
        const next = data.state === 'already' ? 'You’re already in the pack.' : data.emailed ? 'Nearly there: tap Confirm in the email we just sent.' : 'We’ll email you to confirm.';
        form.innerHTML = `<div class="join__done" role="status">${
          data.code
            ? `<span>${next} Your first-order code:</span><strong>${esc(data.code)}</strong><span>Type it in the cart under “Have a code?”.</span>`
            : `<span>${next}</span>`
        }</div>`;
        return;
      }
      if (note) note.textContent = data.error ?? 'That didn’t work — try again in a minute.';
    } catch {
      if (note) note.textContent = 'Check your connection and try again.';
    }
    btn.disabled = false;
  });
}

/* ---------------- header: blends into the studio, solidifies on scroll ---------------- */

function initHeader() {
  const hdr = $('[data-hdr]');
  const stage = $('[data-studio-hero] [data-stage]');
  if (!hdr || !stage) return;
  // Transparent only while the artwork is under it: once the stage has scrolled past the header
  // (on phones the copy and the chips come next), it goes solid. Works in every motion mode.
  // Header height mirrors --header-h in CSS; no layout read at boot.
  const h = matchMedia('(min-width: 960px)').matches ? 76 : 64;
  new IntersectionObserver(([en]) => hdr.classList.toggle('is-solid', !en.isIntersecting), { rootMargin: `-${h}px 0px 0px 0px` }).observe(stage);
}

/* ---------------- the studio: tap a swatch to morph the whole scene ---------------- */

function initStudio() {
  const root = $('[data-studio-hero]');
  if (!root) return;
  const scenes = $$('[data-stage] [data-studio]', root);
  const blurbs = $$('.shero__blurb', root);
  const swatches = $$<HTMLButtonElement>('[data-swatch]', root);
  const counter = $('[data-studio-n]', root);
  if (scenes.length < 2) return;
  let active = 0;
  let busy = false;
  const preload = (i: number) => {
    for (const src of (scenes[i].dataset.pieces ?? '').split(' ').filter(Boolean)) new Image().src = src;
  };
  const show = (next: number) => {
    if (busy || next === active || !scenes[next]) return;
    busy = true;
    const cur = scenes[active];
    const nx = scenes[next];
    for (const k of ['bg', 'ink', 'deep', 'word'] as const) root.style.setProperty(`--${k}`, nx.dataset[k] ?? '');
    cur.classList.add('is-out');
    cur.classList.remove('is-on');
    blurbs[active].hidden = true;
    blurbs[next].hidden = false;
    swatches.forEach((b, i) => b.setAttribute('aria-pressed', String(i === next)));
    if (counter) counter.textContent = String(next + 1);
    const land = () => {
      cur.hidden = true;
      cur.classList.remove('is-out');
      nx.hidden = false;
      nx.classList.add('is-on', 'is-arriving');
      active = next;
      busy = false;
      preload((next + 1) % scenes.length);
    };
    if (reduceMotion) land();
    else setTimeout(land, 330);
  };
  // Auto-cycle is the swatch's progress bar: when its CSS animation ends, move on.
  // No timers, so pausing the animation pauses the cycle, and reduced motion never cycles.
  const stopAuto = () => root.removeAttribute('data-auto');
  root.addEventListener('animationend', (e) => {
    if (e.animationName === 'swatch-progress' && root.hasAttribute('data-auto')) show((active + 1) % scenes.length);
  });
  root.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-swatch]');
    if (!b) return;
    stopAuto();
    show(Number(b.dataset.swatch));
  });
  const pause = (on: boolean) => root.classList.toggle('is-paused', on);
  root.addEventListener('pointerenter', () => pause(true));
  root.addEventListener('pointerleave', () => pause(false));
  root.addEventListener('focusin', () => pause(true));
  root.addEventListener('focusout', () => pause(false));
  new IntersectionObserver(([en]) => root.classList.toggle('is-away', !en.isIntersecting), { threshold: 0.25 }).observe(root);
  document.addEventListener('visibilitychange', () => root.classList.toggle('is-away', document.hidden));
  if (!reduceMotion) {
    root.setAttribute('data-auto', '');
    // The next scene's pieces are fetched only once the page has settled, well
    // clear of the first paint, and still ahead of the 6s auto-cycle.
    addEventListener('load', () => setTimeout(() => preload(1), 2500), { once: true });
  }
}

/* ---------------- deferred tile images: load when near, not 1250px early ---------------- */

// Native lazy-loading starts every print-wall and category tile within ~1250px of the
// viewport on 4G, ahead of the hero. These wait until they are 480px away.
function initDeferred() {
  const imgs = $$<HTMLImageElement>('img[data-src]');
  if (!imgs.length) return;
  const load = (im: HTMLImageElement) => {
    if (im.dataset.srcset) im.srcset = im.dataset.srcset;
    if (im.dataset.sizes) im.sizes = im.dataset.sizes;
    im.src = im.dataset.src!;
    delete im.dataset.src;
  };
  const io = new IntersectionObserver((entries) => {
    for (const en of entries) if (en.isIntersecting) {
      load(en.target as HTMLImageElement);
      io.unobserve(en.target);
    }
  }, { rootMargin: '480px 0px' });
  for (const im of imgs) io.observe(im);
}

/* ---------------- card -> product page morph (cross-document view transitions) ---------------- */

// A product can sit in two rails on one page, and duplicate view-transition
// names cancel the whole transition, so only the card you tap gets named.
function nameCard(card: HTMLElement | null | undefined) {
  const im = card?.querySelector<HTMLElement>('[data-fly]');
  if (im && card?.dataset.id) im.style.viewTransitionName = `p-${card.dataset.id}`;
}
function initMorph() {
  if (reduceMotion || !('startViewTransition' in document)) return;
  document.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="/products/"]');
    if (a) nameCard(a.closest<HTMLElement>('[data-card]'));
  });
  // Coming back from a product page, its card is named before first paint so the hero settles into it.
  addEventListener('pagereveal', () => {
    const from = (window as unknown as { navigation?: { activation?: { from?: { url?: string } } } }).navigation?.activation?.from?.url;
    const slug = from ? /\/products\/([^/?#]+)/.exec(from)?.[1] : null;
    if (slug) nameCard($(`[data-card] a[href="/products/${CSS.escape(slug)}"]`)?.closest<HTMLElement>('[data-card]'));
  });
}

/* ---------------- colour-field cards: tilt + sheen follow the pointer ---------------- */

function initTilt() {
  if (reduceMotion || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  let target: HTMLElement | null = null;
  let raf = 0;
  let px = 0;
  let py = 0;
  const reset = (el: HTMLElement | null) => {
    if (!el) return;
    for (const k of ['--rx', '--ry', '--mx', '--my']) el.style.removeProperty(k);
  };
  document.addEventListener('pointermove', (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>('[data-tilt]');
    if (t !== target) {
      reset(target);
      target = t;
    }
    if (!t) return;
    px = e.clientX;
    py = e.clientY;
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      if (!target) return;
      const r = target.getBoundingClientRect();
      const x = (px - r.left) / r.width;
      const y = (py - r.top) / r.height;
      target.style.setProperty('--ry', `${((x - 0.5) * 12).toFixed(2)}deg`);
      target.style.setProperty('--rx', `${((0.5 - y) * 12).toFixed(2)}deg`);
      target.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
      target.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
    });
  }, { passive: true });
  document.addEventListener('pointerout', (e) => {
    if (!e.relatedTarget) {
      reset(target);
      target = null;
    }
  });
}

/* ---------------- "build the walk": add a whole print set in one tap ---------------- */

function initSetBuilder() {
  const root = $('[data-set-builder]');
  if (!root) return;
  let data: SetPieceData[] = [];
  try {
    data = JSON.parse($('[data-set-data]', root)?.textContent || '[]') as SetPieceData[];
  } catch {
    return;
  }
  const rows = new Map<string, HTMLElement>(data.map((p) => [p.id, $<HTMLElement>(`[data-piece="${p.id}"]`, root)!]));
  const chosen = new Map<string, (string | null)[]>();
  for (const p of data) {
    const ch: (string | null)[] = p.options.map((o) => (o.values.length === 1 ? o.values[0] : null));
    // The shopper's pet size pre-picks sizes across the set.
    if (p.sizeIndex >= 0 && profile.size) {
      const fit = p.options[p.sizeIndex].values.find((v) => sizeKey(v) === profile.size && valueAvailable(p, ch, p.sizeIndex, v));
      if (fit) ch[p.sizeIndex] = fit;
    }
    chosen.set(p.id, ch);
  }
  const picked = (li: HTMLElement) => ($('[data-piece-check]', li) as HTMLInputElement | null)?.checked ?? false;
  const render = () => {
    let total = 0;
    let n = 0;
    let missing = 0;
    for (const p of data) {
      const li = rows.get(p.id)!;
      if ('pending' in li.dataset) continue; // "back online soon": listed, never ticked, priced or sized
      const on = picked(li);
      const ch = chosen.get(p.id)!;
      const v = variantFor(p, ch);
      li.classList.toggle('is-off', !on);
      for (const fs of $$('[data-opt]', li)) {
        const i = Number(fs.dataset.opt);
        for (const b of $$<HTMLButtonElement>('[data-set-value]', fs)) {
          b.setAttribute('aria-pressed', String(ch[i] === b.dataset.setValue));
          b.dataset.unavailable = String(!valueAvailable(p, ch, i, b.dataset.setValue!));
        }
      }
      const priceEl = $('[data-piece-price]', li);
      if (priceEl) priceEl.textContent = v ? money(v.sale ?? v.price) : `from ${money(p.price)}`;
      li.classList.toggle('needs-size', on && !v);
      if (on) {
        n++;
        total += v ? v.sale ?? v.price : p.price; // unsized pieces count at their lowest price
        if (!v) missing++;
      }
    }
    $('[data-set-count]', root)!.textContent = `${n} ${n === 1 ? 'piece' : 'pieces'}`;
    $('[data-set-total]', root)!.textContent = `${missing ? 'from ' : ''}${money(total)}`;
    ($('[data-set-add]', root) as HTMLButtonElement).disabled = n === 0;
    $('[data-set-note]', root)!.textContent = missing ? `Choose a size for ${missing} ${missing === 1 ? 'piece' : 'pieces'} to add the set.` : '';
  };
  root.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-set-value]');
    if (!b) return;
    const li = b.closest<HTMLElement>('[data-piece]')!;
    const i = Number(b.closest<HTMLElement>('[data-opt]')!.dataset.opt);
    const ch = chosen.get(li.dataset.piece!)!;
    ch[i] = ch[i] === b.dataset.setValue ? null : b.dataset.setValue!;
    render();
  });
  root.addEventListener('change', (e) => {
    if ((e.target as HTMLElement).matches('[data-piece-check]')) render();
  });
  $('[data-set-add]', root)!.addEventListener('click', () => {
    const adds: { v: string; pid: string; ga: GaItem }[] = [];
    let first: Element | null = null;
    for (const p of data) {
      const li = rows.get(p.id)!;
      if (!picked(li)) continue;
      const v = variantFor(p, chosen.get(p.id)!);
      if (!v || !v.available) {
        li.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        $('[data-set-value]', li)?.focus();
        toast(`Choose a size for the ${p.name.toLowerCase()} first`);
        return;
      }
      adds.push({ v: v.id, pid: p.id, ga: gaItem(p, v, 1) });
      first ??= $('img', li);
    }
    if (!adds.length) return;
    for (const a of adds) putInCart(a.v, 1, a.pid);
    track('add_to_cart', adds.map((a) => a.ga));
    saveCart();
    refreshCart(true);
    fetchToBag(first).then(() => {
      bump('[data-cart-count]');
      toast(`Bosco fetched ${adds.length} pieces`);
      openDialog('cart');
    });
  });
  render();
}

/* ---------------- boot ---------------- */

renderCounts();
renderFavs();
renderProfile();
initHeader();
initStudio();
initDeferred();
initMorph();
initTilt();
initSetBuilder();
initProduct();
initFilters();
initSearch();
initLists();
initThanks();
initJoin();
initAccount();
// A product page prerendered on a hover (speculation rules) asks for its cart quote only once it's actually shown.
// The cart page itself always renders (an empty cart shows Bosco's empty bowl, not "Loading…").
if (cart.items.length || $('[data-cart-lines]')) {
  if ((document as Document & { prerendering?: boolean }).prerendering) document.addEventListener('prerenderingchange', () => refreshCart(), { once: true });
  else refreshCart();
}
if (location.hash === '#cart') openDialog('cart');
